import * as React from "react";
import { island } from "@/lib/lazyIsland";
import { useLocation } from "@tanstack/react-router";
import { useCartSync } from "@/hooks/useCartSync";
import { useAccountCartRestore } from "@/hooks/useAccountCartRestore";
import { AccountCartRestoreDialog } from "@/components/cart/AccountCartRestoreDialog";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CountryDetectionBanner } from "@/components/CountryDetectionBanner";
import { TopStripSlot } from "@/components/banners/BannerSlot";
import { SiteBreadcrumbs } from "@/components/SiteBreadcrumbs";
import { BreadcrumbsDebugOverlay } from "@/components/BreadcrumbsDebugOverlay";
import { PageBackground } from "@/components/PageBackground";
import { PhoneMessagesOverridesLoader } from "@/components/PhoneMessagesOverridesLoader";
import { RequirePhoneGate } from "@/components/RequirePhoneGate";
import { BirthdayGiftBanner } from "@/components/BirthdayGiftBanner";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { IntroSplash } from "@/components/IntroSplash";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

// ⚡ عزل كود الأدمن: لا يُحمَّل إلا عند زيارة /admin/*
const AdminNav = island(() => import("@/components/admin/AdminNav"), "AdminNav");
const FeatureToggleHeader = island(() => import("@/components/admin/FeatureToggleHeader"), "FeatureToggleHeader");


// ⚡ خارج حزمة الإقلاع: تُحمَّل بعد أوّل رسم (لا تعرض شيئاً قبل تفاعل المستخدم)
const CartDrawer = island(() => import("@/components/CartDrawer"), "CartDrawer");
const GiftFlowOverridesLoader = island(() => import("@/components/GiftFlowOverridesLoader"), "GiftFlowOverridesLoader");

const ClarityLoader = island(() => import("@/components/ClarityLoader"), "ClarityLoader");
const VisitorDialogShowcase = island(() => import("@/components/VisitorDialogShowcase"), "VisitorDialogShowcase");
const OccasionPopupShowcase = island(() => import("@/components/OccasionPopupShowcase"), "OccasionPopupShowcase");


// مسارات لا تُعرض فيها فتات الخبز:
// - "/" الجذر (عنصر واحد فقط، يخفى تلقائياً في المكوّن)
// - /driver (تطبيق سائق مستقلّ)
// - /admin (شريط فتات خاصّ داخل AdminNav)
// - /a و /l (روابط قصيرة تحويليّة)
function shouldHide(pathname: string): boolean {
  if (pathname === "/") return true;
  if (pathname.startsWith("/driver")) return true;
  if (pathname.startsWith("/admin")) return true;
  if (pathname.startsWith("/a/") || pathname.startsWith("/l/")) return true;
  // إيصال الطلب: المسار يحمل توكناً — إظهاره في الفتات عبث
  if (pathname.startsWith("/order/")) return true;
  return false;
}

export function SiteShell({ children }: { children: React.ReactNode }) {
  useCartSync();
  const cartRestore = useAccountCartRestore();
  const { pathname, searchStr } = useLocation();

  const isAdminRoute = pathname.startsWith("/admin");
  /** وضع التضمين: المحرّر معروض داخل iframe في مركز البلوكات → بلا ناف/فوتر/فتات.
   *  نقرأ searchStr الخام لأن validateSearch في بعض صفحات الأدمن يحذف المعاملات
   *  غير المعرّفة (ومنها embed) من كائن search بعد أن يبقى ظاهراً في الرابط. */
  const isEmbed = new URLSearchParams(searchStr).get("embed") === "1";


  // صفحات لها PageHero → الـBreadcrumbs تُعرض داخل PageHero بعد الصورة، لا فوقها.
  const heroRoutePatterns = [/^\/shop$/, /^\/contact$/, /^\/gifts$/, /^\/journal(\/.*)?$/, /^\/wholesale$/, /^\/ambassadors(\/.*)?$/, /^\/collection\/.+$/, /^\/account(\/.*)?$/, /^\/wishlist(\/.*)?$/];
  const hasPageHero = heroRoutePatterns.some((r) => r.test(pathname));
  // صفحة المنتج: بانر الموبايل فوق → الفتات تُعرض أسفل البانر (من PageHero)،
  // وعلى الديسكتوب (لا بانر) تبقى في مكانها الطبيعيّ أعلى المحتوى.
  const isProductRoute = /^\/product\/.+$/.test(pathname);
  const showBreadcrumbs = !shouldHide(pathname) && !hasPageHero;
  const scope: "public" | "account" = pathname.startsWith("/account") ? "account" : "public";

  return (
    <>
      <React.Suspense fallback={null}>
        <GiftFlowOverridesLoader />
      </React.Suspense>
      {pathname.startsWith("/account") ? (
        <>
          <PhoneMessagesOverridesLoader />
          <RequirePhoneGate />
        </>
      ) : null}
      <div
        className="flex min-h-screen w-full max-w-[100vw] flex-col overflow-x-hidden bg-background text-foreground"
        style={isAdminRoute ? undefined : { minHeight: "var(--page-bg-shell-min-height, 100vh)" }}
      >
        {!isAdminRoute && <PageBackground />}
        {!isAdminRoute && (
          <>
            <CookieConsentBanner />
            <React.Suspense fallback={null}>
            <ClarityLoader />
            </React.Suspense>
          </>
        )}

        {!isAdminRoute && <TopStripSlot />}
        {!isAdminRoute && <Header />}
        {!isAdminRoute && (
          <React.Suspense fallback={null}>
            <CartDrawer />
          </React.Suspense>
        )}
        {/* AdminNav خارج <main> حتى يعمل sticky (الـmain فيه overflow-x-hidden) */}
        {isAdminRoute && !isEmbed && (
          <React.Suspense fallback={null}>
            <div className="sticky top-0 z-40 mx-auto w-full max-w-6xl bg-background px-4 pt-4 sm:px-6">
              <AdminNav forceRender />
            </div>
          </React.Suspense>
        )}

        <main
          className={isAdminRoute
            ? "admin-main w-full flex-1 overflow-x-hidden"
            : "relative z-10 w-full flex-1 overflow-x-hidden"}
        >
          {!isAdminRoute && <CountryDetectionBanner />}


          {showBreadcrumbs && !isProductRoute && (
            <div className="mx-auto w-full max-w-6xl px-4 pt-3 pb-1 sm:px-6">
              <SiteBreadcrumbs scope={scope} />
            </div>
          )}
          {showBreadcrumbs && isProductRoute && (
            <div className="hidden md:block">
              <div className="mx-auto w-full max-w-6xl px-4 pt-3 pb-1 sm:px-6">
                <SiteBreadcrumbs scope={scope} />
              </div>
            </div>
          )}
          {isAdminRoute ? (
            <div className="[&>*]:pt-0">
              {!isEmbed && (
                <React.Suspense fallback={null}>
                  <div className="mx-auto w-full max-w-6xl px-4 pt-3 sm:px-6">
                    <FeatureToggleHeader />
                  </div>
                </React.Suspense>
              )}
              {children}
            </div>
          ) : children}
        </main>
        {!isEmbed && (
          <div className="relative z-10">
            <Footer />
          </div>
        )}

        {!isAdminRoute && <BirthdayGiftBanner />}
        {!isAdminRoute && (
          <React.Suspense fallback={null}>
            <VisitorDialogShowcase />
            <OccasionPopupShowcase />
          </React.Suspense>
        )}
        {!isAdminRoute && <MobileBottomNav />}
        {!isAdminRoute && <IntroSplash />}
        {!isAdminRoute && (
          <div
            aria-hidden="true"
            className="relative z-10 w-full shrink-0"
            style={{ height: "var(--page-bg-tail-height, 0px)" }}
          />
        )}
        {/* مساحة سفليّة بارتفاع الناف بار حتى لا يغطّي آخر المحتوى/الفوتر على الموبايل */}
        {!isAdminRoute && (
          <div
            aria-hidden="true"
            className="relative z-10 w-full shrink-0 md:hidden"
            style={{ height: "calc(var(--mobile-nav-height, 4rem) + env(safe-area-inset-bottom, 0px))" }}
          />
        )}
      </div>
      <BreadcrumbsDebugOverlay />
      <AccountCartRestoreDialog
        items={cartRestore.pending}
        isRestoring={cartRestore.isRestoring}
        onRestore={cartRestore.restore}
        onKeepCurrent={cartRestore.dismiss}
      />
    </>
  );
}

